import { useCallback, useMemo } from 'react'
import { TFunction } from 'i18next'
import { CharacterRole } from 'app/frontend/types/custom'

import { AbilitySpell, AbilityType } from 'app/frontend/types/custom/Abilities'
import { EncounterPhase } from 'app/frontend/types/custom/EncounterEvents'
import RaidPlan, {
  RaidPlanActor,
  RaidPlanCast,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'
import {
  getAllSpecializations,
  getClass,
} from 'app/frontend/base/services/characterValues'
import {
  getEncounterTimelineData,
  getGeneralTimelineAbilities,
  getRaidPlanAbilities,
} from 'app/frontend/base/services/raidPlanData'
import { usePageInfo } from 'app/frontend/base/config/queries'
import {
  resolvePhaseTiming,
  resolvePhaseIndexAtTime,
} from '../utils/phaseTiming'
import {
  EncounterAssignmentRow,
  resolveEncounterAssignmentRows,
} from '../utils/encounterAssignmentRows'
type EncounterAbilityRow = EncounterAssignmentRow

type PhaseDivider = {
  name: string
  at: number
  phaseIndex: number
  id: string
  canMoveLeft: boolean
  canMoveRight: boolean
  shortenedBy?: number
}

type PhaseBoundaryHandle = {
  id: string
  index: number
  name: string
  at: number
  min: number
  max: number
}

type SpecializationOption = {
  id: number
  name: string
  classId: number
  className: string
  icon: string
  defaultRole: CharacterRole
}

type UseTeamPlanComputedDataOptions = {
  raidPlan?: RaidPlan
  timeline: RaidPlanTimeline
  selectedType: AbilityType
  t: TFunction
  // When true, the encounter's abilities from every difficulty are merged into a
  // single ability set (deduped by spell id). Used by the dev assignment defaults
  // page so defaults can be authored for a boss even when the difficulty selected
  // has no timeline of its own yet.
  mergeAbilitiesAcrossDifficulties?: boolean
}

type TeamPlanComputedData = {
  encounter:
    | {
        id: number
        name: string
        wclId: number
        thumbnailUrl?: string
        encounterAreaImages?: string[]
        swappablePhases?: number[]
      }
    | undefined
  hasEncounterTimeline: boolean
  maxTime: number
  encounterAbilityRows: EncounterAbilityRow[]
  encounterAssignmentRows: EncounterAbilityRow[]
  phaseDividers: PhaseDivider[]
  phaseBoundaryHandles: PhaseBoundaryHandle[]
  phases: EncounterPhase[]
  phaseStarts: number[]
  phaseEnds: number[]
  effectivePhaseDurations: number[]
  phaseOrder: number[]
  resolvePhaseIndexAtTime: (at: number) => number
  specializations: SpecializationOption[]
  castInfoByActor: Record<
    string,
    {
      duration: number
      cooldown: number
      issue?:
        | { type: 'warning_overlap'; message: string }
        | { type: 'hard_overlap'; message: string }
    }
  >[]
  classColor: (classId?: number) => string
  getActorSpells: (actor: RaidPlanActor) => AbilitySpell[]
  generalAbilities: AbilitySpell[]
  clamp: (value: number, min: number, max: number) => number
}

const EMPTY_PHASES: EncounterPhase[] = []

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

const buildCastRechargeModel = (
  casts: RaidPlanCast[],
  charges: number,
  cooldown: number,
) => {
  const safeCharges = Math.max(1, Math.floor(charges))
  const safeCooldown = Math.max(0, cooldown)
  const sorted = [...casts].sort((a, b) => a.at - b.at)
  const rechargeEnds: number[] = []
  return sorted.reduce<Record<string, { valid: boolean; cooldown: number }>>(
    (acc, cast) => {
      while (rechargeEnds[0] !== undefined && rechargeEnds[0] <= cast.at) {
        rechargeEnds.shift()
      }
      const valid = rechargeEnds.length < safeCharges
      const rechargeStart =
        rechargeEnds.length === 0
          ? cast.at
          : rechargeEnds[rechargeEnds.length - 1]
      const rechargeEnd = rechargeStart + safeCooldown
      rechargeEnds.push(rechargeEnd)
      acc[cast.id] = {
        valid,
        cooldown: Math.floor(Math.max(rechargeEnd - cast.at, 0)),
      }
      return acc
    },
    {},
  )
}

const useTeamPlanComputedData = ({
  raidPlan,
  timeline,
  selectedType,
  t,
  mergeAbilitiesAcrossDifficulties = false,
}: UseTeamPlanComputedDataOptions) => {
  const {
    data: { currentExpansion },
  } = usePageInfo()

  const abilities = useMemo(() => getRaidPlanAbilities(), [])
  const spellsByClassId = useMemo(() => {
    return Object.entries(abilities.classSpells).reduce<
      Record<string, Record<number, AbilitySpell>>
    >((acc, [classId, spells]) => {
      acc[classId] = spells.reduce<Record<number, AbilitySpell>>(
        (map, spell) => {
          map[spell.id] = spell
          return map
        },
        {},
      )
      return acc
    }, {})
  }, [abilities.classSpells])

  const spellsBySpecId = useMemo(() => {
    return Object.entries(abilities.specSpells).reduce<
      Record<string, Record<number, AbilitySpell>>
    >((acc, [specId, spells]) => {
      acc[specId] = spells.reduce<Record<number, AbilitySpell>>(
        (map, spell) => {
          map[spell.id] = spell
          return map
        },
        {},
      )
      return acc
    }, {})
  }, [abilities.specSpells])

  const getActorSpell = useCallback(
    (actor: RaidPlanActor, spellId: number) => {
      const fromSpec = actor.spec_id
        ? spellsBySpecId[actor.spec_id.toString()]?.[spellId]
        : undefined
      if (fromSpec) return fromSpec
      return spellsByClassId[actor.class_id.toString()]?.[spellId]
    },
    [spellsByClassId, spellsBySpecId],
  )

  const getAllActorSpells = useCallback(
    (actor: RaidPlanActor) => {
      const classSpells = spellsByClassId[actor.class_id.toString()] ?? {}
      const specSpells = actor.spec_id
        ? spellsBySpecId[actor.spec_id.toString()] ?? {}
        : {}
      return { ...classSpells, ...specSpells }
    },
    [spellsByClassId, spellsBySpecId],
  )

  const encounter = useMemo(
    () =>
      currentExpansion.seasons
        .flatMap((season) => season.instances)
        .flatMap((instance) => instance.encounters)
        .find((entry) => entry.id === raidPlan?.encounterId),
    [raidPlan?.encounterId],
  )

  const encounterTimeline = useMemo(() => {
    if (!raidPlan) return undefined
    return getEncounterTimelineData()[raidPlan.encounterId.toString()]
  }, [raidPlan])

  const encounterDifficulty = useMemo(() => {
    if (!encounterTimeline || !raidPlan) return undefined
    const primaryDifficulty =
      encounterTimeline.difficulties.find(
        (difficulty) => difficulty.difficulty === raidPlan.difficulty,
      ) ?? encounterTimeline.difficulties[0]

    if (!mergeAbilitiesAcrossDifficulties || !primaryDifficulty) {
      return primaryDifficulty
    }

    // Use the difficulty with the most phases as the timing source so that as
    // many merged ability entries as possible resolve against a phase.
    const phaseSource = encounterTimeline.difficulties.reduce(
      (best, difficulty) =>
        difficulty.phases.length > best.phases.length ? difficulty : best,
      primaryDifficulty,
    )

    // Cast IDs embed the difficulty (e.g. "16827|m|123|p3|3"), so cast overrides
    // authored here (ignored casts, moved casts) only line up with plans of the
    // matching difficulty. Pick each ability's casts from the difficulty it is
    // scoped to so the overrides authored here apply to the plans the ability
    // actually targets. Falls back to the plan's (primary) difficulty when an
    // ability has no scope or its scoped difficulty has no timeline of its own.
    const storedAbilities = timeline.encounter_assignments?.abilities ?? {}
    const preferredDifficultyBySpellId = new Map<number, string>()
    Object.values(storedAbilities).forEach((ability) => {
      if (preferredDifficultyBySpellId.has(ability.spell_id)) return
      const scopedDifficulties = Array.isArray(ability.difficulty)
        ? ability.difficulty
        : ability.difficulty
        ? [ability.difficulty]
        : []
      const scopedDifficulty = scopedDifficulties.find((difficulty) =>
        encounterTimeline.difficulties.some(
          (entry) => entry.difficulty === difficulty,
        ),
      )
      if (scopedDifficulty) {
        preferredDifficultyBySpellId.set(ability.spell_id, scopedDifficulty)
      }
    })

    const mergedAbilities = encounterTimeline.difficulties.reduce<
      typeof primaryDifficulty.abilities
    >((acc, difficulty) => {
      Object.values(difficulty.abilities).forEach((ability) => {
        const key = ability.spell_id.toString()
        const preferredDifficulty =
          preferredDifficultyBySpellId.get(ability.spell_id) ??
          raidPlan.difficulty
        // The scoped (preferred) difficulty's version always wins; otherwise keep
        // the first difficulty that provides this ability.
        if (difficulty.difficulty === preferredDifficulty) {
          acc[key] = ability
        } else if (acc[key] === undefined) {
          acc[key] = ability
        }
      })
      return acc
    }, {})

    return { ...phaseSource, abilities: mergedAbilities }
  }, [
    encounterTimeline,
    raidPlan,
    mergeAbilitiesAcrossDifficulties,
    timeline.encounter_assignments?.abilities,
  ])

  const generalAbilities = useMemo(() => getGeneralTimelineAbilities(), [])

  const {
    defaultPhaseDurations,
    defaultPhaseStarts,
    effectivePhaseDurations,
    phaseStarts,
    phaseEnds,
    phaseOrder,
  } = useMemo(() => {
    if (!encounterDifficulty) {
      return {
        defaultPhaseDurations: [],
        effectivePhaseDurations: [],
        defaultPhaseStarts: [],
        phaseStarts: [],
        phaseEnds: [],
        phaseOrder: [] as number[],
      }
    }
    return resolvePhaseTiming({
      phases: encounterDifficulty.phases,
      persistedPhaseDurations:
        timeline.phase_durations ?? raidPlan?.timeline?.phase_durations,
      phaseOrder: timeline.phase_order ?? raidPlan?.timeline?.phase_order,
    })
  }, [
    encounterDifficulty,
    raidPlan?.timeline?.phase_durations,
    raidPlan?.timeline?.phase_order,
    timeline.phase_durations,
    timeline.phase_order,
  ])

  const swappablePhaseIndices = useMemo(() => {
    const phaseCount = encounterDifficulty?.phases.length ?? 0
    const raw = encounter?.swappablePhases ?? []
    return new Set(
      raw.filter(
        (index) => Number.isInteger(index) && index >= 0 && index < phaseCount,
      ),
    )
  }, [encounter?.swappablePhases, encounterDifficulty?.phases.length])

  const resolvePhaseIndexAtTimeValue = useCallback(
    (at: number) =>
      resolvePhaseIndexAtTime(at, phaseStarts, phaseEnds, phaseOrder),
    [phaseEnds, phaseOrder, phaseStarts],
  )

  const maxTime = useMemo(() => {
    const phasesMax = effectivePhaseDurations.reduce(
      (sum, duration) => sum + duration,
      0,
    )
    if (phasesMax > 0) {
      return Math.max(60, phasesMax)
    }
    if (!encounterDifficulty) {
      return 600
    }
    const roundUpToThirtySeconds = (value: number) => Math.ceil(value / 30) * 30
    const encounterMax = encounterDifficulty.maxTime ?? 0
    const actorMax = timeline.actors.reduce((max, actor) => {
      return actor.casts.reduce((innerMax, cast) => {
        if (cast.spell_id === null || cast.hidden) return innerMax
        return Math.max(innerMax, cast.at)
      }, max)
    }, 0)
    return roundUpToThirtySeconds(Math.max(60, encounterMax, actorMax))
  }, [encounterDifficulty, effectivePhaseDurations, timeline.actors])

  const encounterAbilityRows = useMemo(() => {
    if (!encounterDifficulty) return []
    return resolveEncounterAssignmentRows({
      encounterDifficulty,
      defaultPhaseStarts,
      effectivePhaseDurations,
      phaseStarts,
      phaseEnds,
      unknownAbilityLabel: t('dashboard.team.plans.unknown-ability'),
    })
  }, [
    defaultPhaseStarts,
    effectivePhaseDurations,
    encounterDifficulty,
    phaseEnds,
    phaseStarts,
    t,
  ])

  const encounterAssignmentRows = useMemo(() => {
    if (!encounterDifficulty) return []
    return resolveEncounterAssignmentRows({
      encounterDifficulty,
      defaultPhaseStarts,
      effectivePhaseDurations,
      phaseStarts,
      phaseEnds,
      unknownAbilityLabel: t('dashboard.team.plans.unknown-ability'),
      customAbilityLabel: t('dashboard.team.plans.assignments.custom'),
      abilityOverridesByKey: timeline.encounter_assignments?.abilities,
    })
  }, [
    defaultPhaseStarts,
    effectivePhaseDurations,
    encounterDifficulty,
    phaseEnds,
    phaseStarts,
    t,
    timeline.encounter_assignments?.abilities,
  ])

  const phaseDividers = useMemo(() => {
    if (!encounterDifficulty) return []
    return phaseOrder.flatMap((phaseIndex, displayIndex) => {
      const phase = encounterDifficulty.phases[phaseIndex]
      if (!phase) return []
      const canSwapWithPrevious =
        displayIndex > 0 &&
        swappablePhaseIndices.has(phaseIndex) &&
        swappablePhaseIndices.has(phaseOrder[displayIndex - 1] ?? -1)
      const canSwapWithNext =
        displayIndex < phaseOrder.length - 1 &&
        swappablePhaseIndices.has(phaseIndex) &&
        swappablePhaseIndices.has(phaseOrder[displayIndex + 1] ?? -1)
      const defaultDuration = defaultPhaseDurations[phaseIndex] ?? 0
      const currentDuration = effectivePhaseDurations[phaseIndex] ?? 0
      return [
        {
          name: phase.name,
          at: phaseEnds[phaseIndex] ?? maxTime,
          phaseIndex,
          id: phase.id,
          canMoveLeft: canSwapWithPrevious,
          canMoveRight: canSwapWithNext,
          shortenedBy: Math.max(
            0,
            Math.round(defaultDuration - currentDuration),
          ),
        },
      ]
    })
  }, [
    defaultPhaseDurations,
    effectivePhaseDurations,
    encounterDifficulty,
    maxTime,
    phaseEnds,
    phaseOrder,
    swappablePhaseIndices,
  ])

  const phaseBoundaryHandles = useMemo(() => {
    if (!encounterDifficulty) return []
    return phaseOrder.flatMap((phaseIndex, displayIndex) => {
      const phase = encounterDifficulty.phases[phaseIndex]
      if (!phase) return []
      const prevBoundary =
        displayIndex === 0
          ? 0
          : phaseEnds[phaseOrder[displayIndex - 1] ?? -1] ?? 0
      const currentBoundary = phaseEnds[phaseIndex] ?? prevBoundary
      const defaultPhaseDuration = defaultPhaseDurations[phaseIndex] ?? 0
      return [
        {
          id: phase.id,
          index: phaseIndex,
          name: phase.name,
          at: currentBoundary,
          min: prevBoundary,
          max: prevBoundary + defaultPhaseDuration,
        },
      ]
    })
  }, [defaultPhaseDurations, encounterDifficulty, phaseEnds, phaseOrder])

  const specializations = useMemo(() => {
    return Object.entries(getAllSpecializations())
      .flatMap(([id, spec]) => {
        if (typeof spec.class_id !== 'number') return []
        return [
          {
            id: Number(id),
            name: spec.name,
            classId: spec.class_id,
            className:
              getClass(spec.class_id.toString()) || t('shared.unknown'),
            icon: spec.icon,
            defaultRole: spec.default_role as CharacterRole,
          },
        ]
      })
      .sort((a, b) => {
        if (a.className === b.className) return a.name.localeCompare(b.name)
        return a.className.localeCompare(b.className)
      })
  }, [t])

  const classColor = useCallback((classId?: number) => {
    if (classId === undefined || classId === null) return 'var(--color-primary)'
    const className = getClass(classId.toString())
    if (!className) return 'var(--color-primary)'
    return `var(--color-class-${className.toLowerCase().replace(/\s+/g, '-')})`
  }, [])

  const castInfoByActor = useMemo(() => {
    return timeline.actors.map((actor) => {
      const infoByCastId: Record<
        string,
        {
          duration: number
          cooldown: number
          issue?:
            | { type: 'warning_overlap'; message: string }
            | { type: 'hard_overlap'; message: string }
        }
      > = {}
      const castsBySpell = actor.casts.reduce<Record<number, RaidPlanCast[]>>(
        (acc, cast) => {
          if (cast.spell_id === null) return acc
          if (cast.hidden) return acc
          if (!acc[cast.spell_id]) acc[cast.spell_id] = []
          acc[cast.spell_id].push(cast)
          return acc
        },
        {},
      )

      Object.entries(castsBySpell).forEach(([spellIdRaw, casts]) => {
        const spellId = Number(spellIdRaw)
        const spell = getActorSpell(actor, spellId)
        const duration = spell?.duration ?? 0
        const baseCooldown = Math.max(0, spell?.cooldown ?? 0)
        const baseCharges = Math.max(1, Math.floor(spell?.charges ?? 1))

        const cooldownTalent = (spell?.talents ?? [])
          .flatMap((talent) => {
            const modifiedCooldown = talent.modifies?.cooldown
            if (typeof modifiedCooldown !== 'number') return []
            return [{ name: talent.name, cooldown: modifiedCooldown }]
          })
          .filter((entry) => entry.cooldown < baseCooldown)
          .sort((a, b) => a.cooldown - b.cooldown)[0]

        const chargeTalent = (spell?.talents ?? [])
          .flatMap((talent) => {
            const modifiedCharges = talent.modifies?.charges
            if (typeof modifiedCharges !== 'number') return []
            return [{ name: talent.name, charges: Math.floor(modifiedCharges) }]
          })
          .filter((entry) => entry.charges > baseCharges)
          .sort((a, b) => b.charges - a.charges)[0]

        const talentCharges = chargeTalent?.charges ?? baseCharges
        const talentCooldown = cooldownTalent?.cooldown ?? baseCooldown

        const baseModel = buildCastRechargeModel(
          casts,
          baseCharges,
          baseCooldown,
        )
        const displayModel = buildCastRechargeModel(
          casts,
          talentCharges,
          talentCooldown,
        )
        const chargeModel = buildCastRechargeModel(
          casts,
          talentCharges,
          baseCooldown,
        )
        const cooldownModel = buildCastRechargeModel(
          casts,
          baseCharges,
          talentCooldown,
        )
        const talentModel = buildCastRechargeModel(
          casts,
          talentCharges,
          talentCooldown,
        )

        casts.forEach((cast) => {
          const baseEntry = baseModel[cast.id]
          const displayEntry = displayModel[cast.id]
          const chargeEntry = chargeModel[cast.id]
          const cooldownEntry = cooldownModel[cast.id]
          const talentEntry = talentModel[cast.id]
          if (
            !baseEntry ||
            !displayEntry ||
            !chargeEntry ||
            !cooldownEntry ||
            !talentEntry
          )
            return

          const info: {
            duration: number
            cooldown: number
            issue?:
              | { type: 'warning_overlap'; message: string }
              | { type: 'hard_overlap'; message: string }
          } = {
            duration,
            cooldown: displayEntry.cooldown,
          }

          if (!baseEntry.valid) {
            if (talentEntry.valid) {
              const enabledByCharges = chargeEntry.valid && !baseEntry.valid
              const enabledByCooldown = cooldownEntry.valid && !baseEntry.valid
              const warningTalents = [
                enabledByCharges ? chargeTalent?.name : undefined,
                enabledByCooldown ? cooldownTalent?.name : undefined,
              ].filter((name): name is string => Boolean(name))
              const fallbackTalents = [
                chargeTalent?.name,
                cooldownTalent?.name,
              ].filter((name): name is string => Boolean(name))
              const names = warningTalents.length
                ? warningTalents
                : fallbackTalents
              const talentNamesText =
                names.length > 1
                  ? `${names.slice(0, -1).join(', ')} or ${
                      names[names.length - 1]
                    }`
                  : names[0]
              info.issue = {
                type: 'warning_overlap',
                message: talentNamesText
                  ? `Casts overlap without the ${talentNamesText} talent`
                  : 'Casts overlap without the required talent',
              }
            } else {
              info.issue = {
                type: 'hard_overlap',
                message: 'Casts overlap',
              }
            }
          }

          infoByCastId[cast.id] = info
        })
      })
      return infoByCastId
    })
  }, [getActorSpell, timeline.actors])

  const getActorSpells = useCallback(
    (actor: RaidPlanActor) => {
      return Object.values(getAllActorSpells(actor))
        .filter((spell) => spell.type === selectedType)
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    [getAllActorSpells, selectedType],
  )

  return {
    encounter,
    hasEncounterTimeline: Boolean(encounterDifficulty),
    maxTime,
    encounterAbilityRows,
    encounterAssignmentRows,
    phaseDividers: phaseDividers as PhaseDivider[],
    phaseBoundaryHandles: phaseBoundaryHandles as PhaseBoundaryHandle[],
    phases: encounterDifficulty?.phases ?? EMPTY_PHASES,
    phaseStarts,
    phaseEnds,
    effectivePhaseDurations,
    phaseOrder,
    resolvePhaseIndexAtTime: resolvePhaseIndexAtTimeValue,
    specializations: specializations as SpecializationOption[],
    castInfoByActor,
    classColor,
    getActorSpells,
    generalAbilities,
    clamp,
  }
}

export type {
  EncounterAbilityRow,
  PhaseDivider,
  PhaseBoundaryHandle,
  SpecializationOption,
  TeamPlanComputedData,
}
export default useTeamPlanComputedData
